<template>
  <div v-if="isRequesting" class="loading">
    <oba-loading width="480px" />
  </div>

  <div v-if="!isRequesting" class="collapsible-card">
    <div class="card-header" @click="toggleCollapse">
      <div class="title">
        {{ transformUpperCase(source.displayText ? source.displayText : source.name) }}
      </div>
      <span class="arrow" :class="{ 'collapsed': !isCollapsed }">&#9660;</span>
    </div>

    <div class="card-body" v-show="isCollapsed">
      <div class="filter-wrapper">
        <div class="top">
          <button type="button" :class="['btn', 'secondary', filters.length == 0 ? '' : 'selected']" @click.stop="triggerModalFilter(true)">
            <i v-if="filters.length == 0" class="material-icons left">tune</i>
            <span v-else class="amount">{{filters.length}}</span>
            Filter
          </button>
          
          <oba-query-builder 
            :modalId="uniqueFilterId"
            :displayFilter="displayFilter" 
            @close="triggerModalFilter(false)" 
            :fieldOptions="sortedFieldsType" 
            @applyQuery="onFilterApplied" 
            :initQueries="filters"
            :customStatusOptions="currentCollectionStatuses"> 
          </oba-query-builder>
        </div>

        <div class="bottom" v-if="filters.length > 0">
          <div class="filter-row">
            <span>Filter: </span> 
            <div v-for="filter, i in filters" :key="i" class="info">
              <span class="label">{{`${filter.field} ${filter.operator} ${filter.value != null ? filter.value : '' }`}}</span>
              <i class="material-icons" @click.stop="removeQueryFilter(i)">close</i>
            </div>
          </div>
        </div>

        <div v-if="selectedDocuments.length > 0 && allowedTransitions.length > 0" class="bulk-actions">
          <ul class="menus">
            <li v-for="(status, index) in allowedTransitions" :key="index">
              <a @click="changeStatus(status)">{{ transformUpperCase(status.display) }}</a>
            </li>
          </ul>
        </div>
      </div>
      <div class="table">
        <oba-table-sub-items :collection="source.collection" :header="tableHeader" :body="tableBody" @selectRow="onSelectedRow" @selectDoc="onSelectedDocument" />
        <oba-pagination :initPage="selectedPage" :maxPage="maxPage" @changePage="onPageChanged" />
        <div v-if="createPermission" class="create-subitem" @click="toggleCreateTicket(true)">Add {{ transformUpperCase(source.displayName ? source.displayName : source.name) }}</div>
      </div>
    </div>
  </div>

  <oba-modal 
    class="modal-bulk-progress" 
    :id="`bulk-modal-${uniqueFilterId}`" 
    type="medium" 
    :show="displayBulkProgress" 
    :dismissible="!isBulkUpdating"
    @close="handleCloseBulkModal"
  >
    <template v-slot:modal-content>
      <div v-if="isBulkUpdating" class="bulk-loading-state" style="text-align: center; padding-top: 10px;">
        <oba-circle-loading />
        <h5 style="margin-top: 15px; font-size: 18px;">Processing {{ currentBulkIndex }} / {{ totalBulkItems }} tickets...</h5>
        <p style="color: #757575; font-size: 14px; margin-top: 5px;">Please do not close this window.</p>
      </div>

      <div v-else class="bulk-result-state">
        <div v-if="bulkFailedItems.length === 0" style="text-align: center;">
          <i class="material-icons" style="font-size: 40px; color: #4CAF50;">check_circle</i>
          <h5 style="margin-top: 10px; font-size: 18px;">Update success!</h5>
          <p style="font-size: 14px; margin-top: 5px;">All {{ totalBulkItems }} tickets have been updated.</p>
        </div>

        <div v-else>
          <div style="text-align: center; margin-bottom: 15px;">
            <i class="material-icons" style="font-size: 40px; color: #FF9800;">info</i>
            <h5 style="margin-top: 10px; font-size: 18px;">Update partially success</h5>
            <p style="font-size: 14px; margin-top: 5px;">Some tickets cannot be updated:</p>
          </div>
          
          <div style="background: #f9f9f9; border-radius: 4px; padding: 10px; border: 1px solid #e0e0e0;">
            <ul style="max-height: 150px; overflow-y: auto; padding-right: 5px; margin: 0;">
              <li v-for="(item, index) in bulkFailedItems" :key="index" style="color: #d32f2f; margin-bottom: 6px; font-size: 13px; line-height: 1.4; border-bottom: 1px dashed #e0e0e0; padding-bottom: 4px;">
                - {{ item.summary || "No Summary" }} 
                <span style="font-size: 11px; color: #757575;">(Error: {{ item.reason }} | ID: {{ item.docId }})</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>
  </oba-modal>

  <oba-create-ticket :visible="displayCreate" :collection="source.collection" :subItem="source" @closeCreate="toggleCreateTicket(false)" @onCreate="onCreateDocument">
  </oba-create-ticket>
  <oba-modal class="modal-transition" :id="uniqueTransitionId" :show="showTransition" @close="toggleTransition(false)">
    <template v-slot:modal-content>
      <div class="title-modal">
        <h5>{{ currentTransition }}</h5>
      </div>
      <div class="mid-content">
        <oba-form-builder :key="formKey" ref="fieldForm" :fields="fieldLists" />
      </div>
    </template>
    <template v-slot:modal-footer>
      <div class="buttons">
        <oba-button class="btn-cancel" category="secondary" label="Cancel" @onClick="toggleTransition(false)" />
        <oba-button category="primary" :label="currentTransition" :isDisabled="isRequesting" :isLoading="isRequesting" @onClick="submitData" />
      </div>
    </template>
  </oba-modal>
</template>

<script>
import { transformDataTable, transformUpperCase, underscoreRemover, convertFromDataForm } from '@/lib/transform.js'
import { showNotification } from '@/lib/utils'
import { generateProps } from '@/lib/propsGenerator'
import { createNamespacedHelpers } from 'vuex'
import { doRequest } from '@/lib/request/index.js'
import apiUrls from '@/lib/request/urls.js'
import tableSubItems from '@/components/tableSubItems'
import Loading from '@/components/loading'
import createTicket from '@/components/createTicket'
import pagination from '@/components/pagination'
import queryBuilder from '@/views/queryBuilder'
import CircleLoading from '@/components/circleLoading'
import ObaModal from '@/components/modal'
import ObaButton from '@/components/button'
import FormBuilder from '@/components/formBuilder'

const { mapState: mapStateConfig, mapGetters: mapGettersConfig, mapMutations: mapMutationsConfig } = createNamespacedHelpers('config')
const { mapState: mapStateUser, mapGetters: mapGettersUser } = createNamespacedHelpers('user')

export default {
  name: "oba-sub-item",
  components: {
    'oba-table-sub-items': tableSubItems,
    'oba-loading': Loading,
    'oba-create-ticket': createTicket,
    'oba-pagination': pagination,
    'oba-query-builder': queryBuilder,
    'oba-circle-loading': CircleLoading,
    'oba-modal': ObaModal,
    'oba-button': ObaButton,
    'oba-form-builder': FormBuilder
  },
  setup(){
    async function getListTickets(projectName, collectionName, userFilters = []){
      const combinedFilters = [
        {
          field: this.source.childField,
          operator: "equal",
          value: this.source.parentValue
        }
      ]
      
      userFilters.forEach(item => {
        let value = item.value
        
        if(item.field.toLowerCase() == 'assignee' && value){
          value = this.getUidByEmail(value) 
        }

        if(this.getDataType(item.field, collectionName) == 'datetime'){
          value = transformToIsoString(item.value)
        }

        if((item.operator == "isEmpty") || (item.operator == "isNotEmpty")){
          combinedFilters.push({
            field: item.field,
            operator: item.operator
          })
        } else {
          combinedFilters.push({
            field: item.field,
            operator: item.operator,
            value: value
          })
        }
      })

      const config = {
        url: apiUrls.listDocument(projectName, collectionName),
        method: 'post',
        data: {
          page: this.selectedPage,
          filters: combinedFilters
        }
      }
      const response = await doRequest(config, {needToken: true})
      return response
    }
    const requestFieldsType = async (projectName, collection) => {
      const config = {
        url: apiUrls.fieldType(projectName, collection),
        method: 'get'
      }
      return await doRequest(config, { needToken: true })
    }

    const editTicket = async (projectName, collection, docId, data) => {
      const config = {
        url: apiUrls.editTicket(projectName, collection, docId),
        method: 'put',
        data: data
      }
      return await doRequest(config, { needToken: true })
    }

    const uploadFile = async (projectName, collection, docId, formData) => {
      const config = {
        url: apiUrls.uploadFile(projectName, collection, docId),
        method: 'post',
        headers: { "Content-Type": "multipart/form-data" },
        data: formData
      }
      return await doRequest(config, { needToken: true })
    }

    return { getListTickets, requestFieldsType, editTicket, uploadFile }
  },
  props: {
    source: {
      type: Object,
      required: false,
      default: {}
      // properties:
      // {
      //   "name": ""
      //   "type": "",
      //   "collection": "",
      //   "childField": "",
      //   "parentField": "",
      //   "table": []
      // }
    }
  },
  data(){
    return {
      tableBody: [],
      rawDocuments: [],
      tableHeader: [],
      selectedPage: 1,
      maxPage: 1,
      isRequesting: false,
      displayCreate: false,
      isCollapsed: false,
      displayFilter: false,
      selectedDocuments: [],
      showTransition: false,
      currentTransition: '',
      changedStatusTo: '',
      fieldLists: [],
      formKey: 0,
      filters: [],
      uidSuffix: Math.random().toString(36).substring(2, 9),
      displayBulkProgress: false,
      isBulkUpdating: false,
      currentBulkIndex: 0,
      totalBulkItems: 0,
      bulkFailedItems: [],
    }
  },
  beforeMount(){
    this.loadData()
  },
  computed: {
    ...mapStateConfig([
      'projectName',
      'collections',
    ]),
    ...mapStateUser([
      'pageState',
    ]),
    ...mapGettersConfig([
      'getSelectedCollection',
      'getCreatePermission',
      'getCollectionEntries',
      'getDataType',
      'getTransitionStatus',
      'getFieldStatus'
    ]),
    ...mapGettersUser([
      'getUidByEmail'
    ]),
    createPermission(){
      return this.getCreatePermission(this.source.collection)
    },
    sortedFieldsType() {
      let sortedData = {}
      const entryList = this.getCollectionEntries(this.source.collection)
      if(entryList){
        entryList.forEach(item => {
          sortedData[item.name] = this.getDataType(item.name, this.source.collection)
        })
      }
      return sortedData
    },
    statusField() {
      return this.getFieldStatus(this.source.collection)
    },
    allowedTransitions() {
      if (this.selectedDocuments.length === 0 || !this.statusField) return []

      const selectedStatuses = this.selectedDocuments.map(docId => {
        const doc = this.rawDocuments.find(d => d.id === docId)
        return doc ? doc[this.statusField] : null
      }).filter(Boolean)

      const uniqueStatuses = [...new Set(selectedStatuses)]

      // Different statuses selected → hide all transitions
      if (uniqueStatuses.length !== 1) return []

      return this.getTransitionStatus(this.source.collection, uniqueStatuses[0])
    },
    uniqueTransitionId() {
      const collectionKey = this.source.collection ? this.source.collection.replace(/\s+/g, '-') : 'default';
      return `transition-modal-${collectionKey}-${this.uidSuffix}`;
    },
    uniqueFilterId() {
      const collectionKey = this.source.collection ? this.source.collection.replace(/\s+/g, '-') : 'default';
      return `filter-modal-${collectionKey}-${this.uidSuffix}`;
    },
    currentCollectionStatuses() {
      if (!this.collections || !this.source.collection) return [];
      const targetCollection = this.collections.find(col => col.name === this.source.collection);
      return targetCollection ? targetCollection.statuses : [];
    }
  },
  methods: {
    ...mapMutationsConfig({
      'saveFieldsType': 'SAVE_FIELDS_TYPE_TO_COLLECTION',
    }),
    transformUpperCase,
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    /**
     * Get list documents from server and transform it to table body
     */
    async getListDocuments(){
      this.isRequesting = true
      const response = await this.getListTickets(this.projectName, this.source.collection, this.filters)
      if(response.status == 201){
        this.rawDocuments = response.data.data
        this.tableBody = transformDataTable(this.tableHeader, response.data.data, this.source.collection)
        this.maxPage = response.data.lastPage || 1
      } 
      this.isRequesting = false
    },
    onSelectedRow(selectedRow){
      this.$router.push({
        name: "detailpage", 
        params: { collection: this.source.collection, docId: selectedRow.docId },
      })
    },
    async loadData(){
      await this.getFieldsType()
      this.tableHeader = this.source.table
      this.getListDocuments()
    },
    toggleCreateTicket(value){
      this.displayCreate = value
    },
    onCreateDocument(){
      this.loadData()
    },
    onPageChanged(page){
      this.selectedPage = page
      this.getListDocuments()
    },
    /**
     * Get fields type from server by projectName and collections
     */
    async getFieldsType(){
      const selectedCollection = this.getSelectedCollection(this.source.collection)
      if(!selectedCollection.fieldsType){
        const fieldsType = await this.requestFieldsType(this.projectName, this.source.collection)
      
        if (fieldsType.status != 200) {
          if(fieldsType.data.error.reason){
            showNotification('error', `${fieldsType.data.error.reason} (${fieldsType.status})`)
          }
          return
        }
        const cleanedFieldsType = underscoreRemover(fieldsType.data)
        const data = {
          collection: this.source.collection,
          fieldsType: cleanedFieldsType
        }
        this.saveFieldsType(data)
      }
    },
    triggerModalFilter(value){
      this.displayFilter = value
    },
    onFilterApplied(filters){
      this.selectedPage = 1
      this.filters = filters
      this.getListDocuments()
      this.triggerModalFilter(false)
    },
    onSelectedDocument(selectedDoc) {
      this.selectedDocuments = selectedDoc
    },
    changeStatus(status) {
      this.currentTransition = status.display
      this.changedStatusTo = status.to
      if (status.hasOwnProperty('screen') && status.screen.length != 0) {
        this.generateFields(status)
        this.toggleTransition(true)
      } else {
        this.bulkUpdateStatus()
      }
    },
    toggleTransition(value) {
      this.showTransition = value
    },
    async generateFields(status) {
      this.fieldLists = await generateProps(status.screen)

      // Auto-fill fields from the first selected document
      if (this.selectedDocuments.length > 0) {
        const firstDocId = this.selectedDocuments[0]
        const doc = this.rawDocuments.find(d => d.id === firstDocId || d.docId === firstDocId)
        if (doc) {
          this.fieldLists = this.fieldLists.map(field => {
            const value = doc[field.id]
            if (value !== undefined && value !== null) {
              return { ...field, inputValue: value }
            }
            return field
          })
        }
      }

      this.formKey++
    },
    async submitData() {
      const data = this.collectData()
      if (!data) return

      this.toggleTransition(false)

      this.totalBulkItems = this.selectedDocuments.length
      this.currentBulkIndex = 0
      this.bulkFailedItems = []
      this.displayBulkProgress = true
      this.isBulkUpdating = true

      for (let i = 0; i < this.selectedDocuments.length; i++) {
        const docId = this.selectedDocuments[i]
        this.currentBulkIndex = i + 1

        try {
          for (const [key, value] of Object.entries(data.attachment)) {
            if (value.length > 0) {
              let formData = new FormData()
              for (const file of value) {
                formData.append(key, file)
                await this.uploadFile(this.projectName, this.source.collection, docId, formData)
              }
            }
          }

          const res = await this.editTicket(this.projectName, this.source.collection, docId, data.field)
          if (res.status !== 200) {
            throw new Error(`Status Code: ${res.status}`)
          }
        } catch (error) {
          const tableItem = this.tableBody.find(t => t.docId === docId);
          let itemSummary = docId;

          if (tableItem && tableItem.columns) {
            const summaryColumn = tableItem.columns.find(c => c.fieldName === 'summary');
            console.log(summaryColumn)
            if (summaryColumn && summaryColumn.value) {
              itemSummary = summaryColumn.value;
            }
          }
          
          if (itemSummary === docId) {
            const rawItem = this.rawDocuments.find(d => d.id === docId || d.docId === docId);
            if (rawItem && (rawItem.summary)) {
              itemSummary = rawItem.summary;
            }
          }

          this.bulkFailedItems.push({
            docId,
            summary: itemSummary,
            reason: error.response?.data || error.message || 'Unknown error'
          })
        }
      }

      this.isBulkUpdating = false
      this.selectedDocuments = []
      this.getListDocuments()
    },
    collectData() {
      let data = {}
      let fieldData = {}
      let attachmentData = {}
      const form = this.$refs['fieldForm']
      const rawData = form.collectValue()
      if (!rawData) return null

      for (const [key, value] of Object.entries(rawData)) {
        const type = this.getDataType(key, this.source.collection)
        if (type != "attachment") {
          fieldData[key] = value
        } else {
          attachmentData[key] = value
        }
      }
      data["field"] = convertFromDataForm(fieldData, this.source.collection)
      data["field"][this.statusField] = this.changedStatusTo
      data["attachment"] = attachmentData
      return data
    },
    async bulkUpdateStatus(extraData = null) {
      this.totalBulkItems = this.selectedDocuments.length
      this.currentBulkIndex = 0
      this.bulkFailedItems = []
      this.displayBulkProgress = true
      this.isBulkUpdating = true 

      let data = extraData || {}
      if (!extraData) {
        data[this.statusField] = this.changedStatusTo
      }

      for (let i = 0; i < this.selectedDocuments.length; i++) {
        const docId = this.selectedDocuments[i]
        this.currentBulkIndex = i + 1

        try {
          const response = await this.editTicket(this.projectName, this.source.collection, docId, data)
          
          if (response.status !== 200) {
            throw new Error(`Status Code: ${response.status} - ${response.data}`)
          }

        } catch (error) {
          const tableItem = this.tableBody.find(t => t.docId === docId);
          let itemSummary = docId;

          if (tableItem && tableItem.columns) {
            const summaryColumn = tableItem.columns.find(c => c.fieldName === 'summary' || c.fieldName === 'nama');
            if (summaryColumn && summaryColumn.value) {
              itemSummary = summaryColumn.value;
            }
          }
          
          if (itemSummary === docId) {
            const rawItem = this.rawDocuments.find(d => d.id === docId || d.docId === docId);
            if (rawItem && (rawItem.summary || rawItem.nama)) {
              itemSummary = rawItem.summary || rawItem.nama;
            }
          }

          this.bulkFailedItems.push({
            docId,
            summary: itemSummary,
            reason: error.response?.data || error.message || 'Unknown error'
          })
        }
      }

      this.isBulkUpdating = false
      this.selectedDocuments = []
      this.getListDocuments() 
    },
    removeQueryFilter(index) {
      this.selectedPage = 1
      this.filters.splice(index, 1)
      this.getListDocuments()
    },
    handleCloseBulkModal(value) {
      if (this.isBulkUpdating) {
        return; 
      }
      this.closeBulkModal();
    },
    closeBulkModal() {
      this.displayBulkProgress = false;
      this.bulkFailedItems = [];
      this.filters = [];
      this.selectedPage = 1;
      this.getListDocuments();
    },
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>